import { EditIcon } from "@chakra-ui/icons";
import {
  AlertDialog,
  AlertDialogOverlay,
  Button,
  HStack,
  useDisclosure,
} from "@chakra-ui/react";
import { ReactNode, RefObject, useRef } from "react";
import EmptyPlaceholder from "components/Shared/EmptyPlaceholder";
import LabeledValue from "components/Shared/LabeledValue";
import TooltipIconButton from "components/Shared/TooltipIconButton";

type RenderDialogProps = {
  cancelRef: RefObject<HTMLButtonElement>;
  onClose: () => void;
};

type Props = {
  assignButtonLabel: string;
  associatedContent?: ReactNode;
  associatedValue: ReactNode;
  editActionLabel?: string;
  emptyLabel: string;
  isAssociated: boolean;
  isEditable?: boolean;
  label: string;
  renderEditDialog: (params: RenderDialogProps) => ReactNode;
};

export default function EditableAssociationAction({
  assignButtonLabel,
  associatedContent,
  associatedValue,
  editActionLabel = "Edit",
  emptyLabel,
  isAssociated,
  isEditable = true,
  label,
  renderEditDialog,
}: Props) {
  const {
    isOpen: isEditing,
    onOpen: onOpenEdit,
    onClose: onCloseEdit,
  } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  return (
    <>
      <LabeledValue
        label={label}
        value={
          isAssociated ? (
            <>
              {associatedValue}

              {(isEditable || associatedContent) && (
                <HStack spacing={1} display="inline-flex" ml={2}>
                  {associatedContent}
                  {isEditable && (
                    <TooltipIconButton
                      icon={<EditIcon />}
                      label={editActionLabel}
                      tooltipProps={{ placement: "bottom" }}
                      variant="ghost"
                      fontSize=".85em"
                      mt="-.4em"
                      onClick={onOpenEdit}
                    />
                  )}
                </HStack>
              )}
            </>
          ) : (
            <>
              <EmptyPlaceholder>{emptyLabel}</EmptyPlaceholder>

              {isEditable && (
                <Button
                  size="xs"
                  variant="outline"
                  leftIcon={<EditIcon />}
                  ml={2}
                  onClick={onOpenEdit}
                >
                  {assignButtonLabel}
                </Button>
              )}
            </>
          )
        }
        py={0}
      />

      <AlertDialog
        isOpen={isEditing}
        onClose={onCloseEdit}
        leastDestructiveRef={cancelRef}
      >
        <AlertDialogOverlay>
          {renderEditDialog({ cancelRef, onClose: onCloseEdit })}
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
